import React from "react";
import { Helmet } from "react-helmet";
import { FaCode, FaDatabase, FaServer, FaTerminal, FaTools, FaLaptopCode, FaExternalLinkAlt } from "react-icons/fa";

function LabSoftware() {
  const softwareCategories = [
    {
      category: "Programming & IDEs",
      icon: <FaCode />,
      software: [
        { name: "Visual Studio Code", desc: "A robust code editor for web and cloud development.", link: "https://code.visualstudio.com/" },
        { name: "Eclipse / IntelliJ Community", desc: "Powerful IDEs for Java and enterprise development.", link: "https://www.eclipse.org/" },
        { name: "Python (IDLE/Jupyter)", desc: "Standard language for Data Science and Scripting.", link: "https://www.python.org/" },
        { name: "GCC / G++ Compiler", desc: "The standard GNU Compiler Collection for C and C++.", link: "https://gcc.gnu.org/" },
        { name: "Android Studio", desc: "Official IDE for Android application development.", link: "https://developer.android.com/studio" }
      ]
    },
    {
      category: "Database & Servers",
      icon: <FaDatabase />,
      software: [
        { name: "MySQL Community Edition", desc: "World's most popular open-source relational database.", link: "https://dev.mysql.com/downloads/" },
        { name: "PostgreSQL", desc: "Advanced open-source object-relational database system.", link: "https://www.postgresql.org/" },
        { name: "XAMPP / WAMP", desc: "Local server environments for PHP development.", link: "https://www.apachefriends.org/" },
        { name: "MongoDB Community", desc: "Leading NoSQL document-oriented database.", link: "https://www.mongodb.com/try/download/community" }
      ]
    },
    {
      category: "Operating Systems",
      icon: <FaServer />,
      software: [
        { name: "Ubuntu Desktop/Server", desc: "The most popular Linux distro for development.", link: "https://ubuntu.com/download" },
        { name: "Fedora / Debian", desc: "Stable and cutting-edge Linux for development.", link: "https://getfedora.org/" },
        { name: "Oracle VirtualBox", desc: "Virtualization software for running multiple OS.", link: "https://www.virtualbox.org/" }
      ]
    },
    {
      category: "Web & Graphics",
      icon: <FaLaptopCode />,
      software: [
        { name: "Node.js & NPM", desc: "JavaScript runtime for scalable applications.", link: "https://nodejs.org/" },
        { name: "GIMP", desc: "Open-source alternative to Photoshop.", link: "https://www.gimp.org/" },
        { name: "Inkscape", desc: "Professional vector graphics editor.", link: "https://inkscape.org/" }
      ]
    },
    {
      category: "System & Network Tools",
      icon: <FaTerminal />,
      software: [
        { name: "Git", desc: "Distributed version control system.", link: "https://git-scm.com/" },
        { name: "Wireshark", desc: "The world's foremost network protocol analyzer.", link: "https://www.wireshark.org/" },
        { name: "FileZilla", desc: "Free FTP solution for cross-server file transfer.", link: "https://filezilla-project.org/" }
      ]
    },
    {
      category: "Office & Documentation",
      icon: <FaTools />,
      software: [
        { name: "LibreOffice", desc: "Powerful, free, and open-source office suite.", link: "https://www.libreoffice.org/" },
        { name: "Moodle", desc: "Learning management system for online courses.", link: "https://moodle.org/" }
      ]
    }
  ];

  return (
    <>
      <Helmet>
        <title>Lab Software - VBU MCA</title>
      </Helmet>
      <div className="PageContainer">
        <div id="Heading">
          <h1>Lab Software (Open Source & Free)</h1>
        </div>
        <div className="SoftwareListSection" data-aos="fade-up">
          <p className="SoftwareDesc">
            As an AICTE-approved institution, we encourage the use of Open Source software. Here are the official download links for the tools used in our labs.
          </p>
          
          <div className="SoftwareGrid">
            {softwareCategories.map((cat, idx) => (
              <div key={idx} className="SoftwareCategoryCard" data-aos="fade-up" data-aos-delay={idx * 50}>
                <div className="CategoryHeader">
                  <span className="CategoryIcon">{cat.icon}</span>
                  <h3>{cat.category}</h3>
                </div>
                <div className="SoftwareItems">
                  {cat.software.map((sw, sIdx) => (
                    <div key={sIdx} className="SoftwareEntry">
                      <div className="SoftwareInfo">
                        <span className="SoftwareName">{sw.name}</span>
                        <p className="SoftwareItemDesc">{sw.desc}</p>
                      </div>
                      <a 
                        href={sw.link} 
                        target="_blank" 
                        rel="noreferrer" 
                        className="OfficialLink"
                      >
                        Official Site <FaExternalLinkAlt size={12} />
                      </a>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      <style>{`
        .SoftwareListSection {
          max-width: 1200px;
          margin: 30px auto 60px auto;
        }
        .SoftwareDesc {
          font-size: 1.05rem;
          color: #555;
          margin-bottom: 35px;
          line-height: 1.6;
          text-align: center;
          max-width: 800px;
          margin-left: auto;
          margin-right: auto;
        }
        .SoftwareGrid {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(340px, 1fr));
          gap: 28px;
        }
        .SoftwareCategoryCard {
          background: #fff;
          border-radius: 14px;
          box-shadow: 0 4px 18px rgba(0,0,0,0.07);
          border: 1px solid #f0f0f0;
          overflow: hidden;
          transition: transform 0.3s ease, box-shadow 0.3s ease;
        }
        .SoftwareCategoryCard:hover {
          transform: translateY(-5px);
          box-shadow: 0 10px 30px rgba(0,0,0,0.11);
          border-color: #1a237e;
        }
        .CategoryHeader {
          display: flex;
          align-items: center;
          gap: 14px;
          background: #1a237e;
          color: #fff;
          padding: 16px 22px;
        }
        .CategoryHeader h3 {
          margin: 0;
          font-size: 1.1rem;
          font-weight: 700;
          color: #fff;
        }
        .CategoryIcon {
          font-size: 1.5rem;
          display: flex;
          align-items: center;
        }
        .SoftwareItems {
          padding: 10px 0;
        }
        .SoftwareEntry {
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: 12px 22px;
          border-bottom: 1px solid #f5f5f5;
          gap: 12px;
        }
        .SoftwareEntry:last-child {
          border-bottom: none;
        }
        .SoftwareInfo {
          flex: 1;
        }
        .SoftwareName {
          display: block;
          font-weight: 700;
          color: #2c3e50;
          font-size: 0.97rem;
          margin-bottom: 3px;
        }
        .SoftwareItemDesc {
          margin: 0;
          font-size: 0.83rem;
          color: #7f8c8d;
          line-height: 1.4;
        }
        .OfficialLink {
          display: inline-flex !important;
          align-items: center;
          gap: 5px;
          white-space: nowrap;
          padding: 6px 14px;
          background: #f0f4ff;
          color: #1a237e !important;
          border-radius: 20px;
          font-size: 0.8rem;
          font-weight: 600;
          border: 1px solid #c5cae9;
          transition: all 0.25s ease;
          flex-shrink: 0;
        }
        .OfficialLink:hover {
          background: #1a237e;
          color: #fff !important;
          border-color: #1a237e;
        }
        @media (max-width: 768px) {
          .SoftwareGrid {
            grid-template-columns: 1fr;
          }
          .SoftwareEntry {
            flex-direction: column;
            align-items: flex-start;
            gap: 10px;
          }
        }
      `}</style>
    </>
  );
}

export default LabSoftware;
